import { useEffect } from "react";
import { isApiReachable } from "@/lib/api";

/**
 * Nightly self-refresh for screens that are never powered off.
 *
 * The TVs run 24/7 while the unit trades 10:00–18:00, so nothing ever gives
 * the browser a fresh start. Over weeks that shows up as renderer memory
 * growth in VIDAA's embedded browser and, more insidiously, as a Realtime
 * socket that has died without firing an `offline` event — the screen keeps
 * playing yesterday's menu perfectly while ignoring every config change.
 * A reload clears both.
 *
 * Deliberately clock-driven rather than a 24h setTimeout: long timers drift
 * and don't survive the browser throttling a background tab. We poll the
 * clock every minute and record the date we last refreshed, so a missed
 * window (TV asleep, power blip) simply refreshes on the next pass.
 */

const REFRESH_HOUR = 4; // 04:00 local — six hours before the doors open.
const TRADING_START_HOUR = 9; // Hard guard: never reload between 09:00
const TRADING_END_HOUR = 19; // and 18:59, whatever the clock says.
const CHECK_INTERVAL_MS = 60_000;

const refreshKey = (screenId: string) => `signage.lastRefreshOn.${screenId}`;

/** Local (not UTC) YYYY-M-D — the TV's own day is what matters here. */
function localDateKey(d: Date): string {
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
}

export function useScheduledRefresh(screenId: string, onRefresh: () => void) {
  useEffect(() => {
    // Stagger by screen: screen 1 at 04:00, screen 2 at 04:01 … screen 7 at
    // 04:06, so seven kiosks don't reconnect to Supabase in the same second.
    const staggerMinute = (Math.max(1, Number(screenId) || 1) - 1) % 60;

    async function tick() {
      const now = new Date();
      const hour = now.getHours();

      if (hour >= TRADING_START_HOUR && hour < TRADING_END_HOUR) return;
      if (hour !== REFRESH_HOUR) return;
      if (now.getMinutes() < staggerMinute) return;

      const today = localDateKey(now);
      try {
        if (localStorage.getItem(refreshKey(screenId)) === today) return;
      } catch {
        // Storage unavailable — refuse to refresh rather than risk a loop.
        return;
      }

      // navigator.onLine only reflects the network interface, not whether the
      // internet path actually works. A real request confirms it. Skipping
      // this tick and trying again next minute (still within the same hour)
      // is what a dead-in-the-water screen needed: the old code trusted
      // onLine, navigated away regardless, and hit the browser's own error
      // page during a dip — silent for 14 hours until someone found it.
      if (!(await isApiReachable())) {
        console.log("Scheduled refresh skipped — API unreachable, will retry next minute");
        return;
      }

      try {
        // Written *before* reloading: if the reload lands back inside the
        // same minute, the flag is what stops it looping.
        localStorage.setItem(refreshKey(screenId), today);
      } catch {
        return;
      }

      console.log("Scheduled nightly refresh");

      // Sweep the media cache too, but leave localStorage intact: it holds
      // the last-known-good config that renders instantly on the way back up.
      if ("caches" in window) {
        caches
          .keys()
          // Own caches only — the Cache API is shared with every app on
          // this origin, including the admin dashboard and the main site.
          .then((keys) => Promise.all(keys.filter((k) => k.startsWith("signage-")).map((k) => caches.delete(k))))
          .catch(() => undefined)
          .then(() => onRefresh());
      } else {
        onRefresh();
      }
    }

    const id = setInterval(tick, CHECK_INTERVAL_MS);
    return () => clearInterval(id);
  }, [screenId, onRefresh]);
}
